import { PercentageOutlined } from '@ant-design/icons';
import { type TableProps, Tag, Tooltip } from 'antd';
import React, { type JSX } from 'react';

import { DeactivateAction } from '@client/components/actions/deactivate.action';
import { EditAction } from '@client/components/actions/edit.action';
import { StatusTag } from '@client/components/StatusTag';
import { EAbilitySubjects } from '@client/hooks/useAbilities';
import { FeatureAssignments } from '@client/pages/subscriptions/FeatureAssignments';
import { getTranslatedMessage } from '@client/utils/i18n.util';
import { actionField } from '@client/utils/table/action.util';
import { columnsCache } from '@client/utils/table/column.util';
import {
  columnFilter,
  type TColumnFilter,
} from '@client/utils/table/filter.util';
import { columnSorter } from '@client/utils/table/sorter.util';
import { indexColumn } from '@client/utils/table/table.util';
import { numberToCurrency, tsToLocaleDateTime } from '@common/utils';

import type { TFilters, TSorts } from '@client/hooks/useTable';
import type {
  ECurrency,
  ELocales,
  EStatus,
  IMetadata,
  IPrice,
  ISubscription,
} from '@common/types';

type TArgs = {
  locale: ELocales;
  sortedInfo: TSorts;
  filteredInfo: TFilters;
  entities: ISubscription[];
  onDelete?: (entity: ISubscription) => void;
  onEdit?: (id: string) => void;
};

export const columnsMetadata = (
  props: TArgs,
  filterIn: string[] = [],
): TableProps<ISubscription>['columns'] => {
  const { locale, sortedInfo, entities, filteredInfo, onEdit, onDelete } =
    props;

  const { cached, updateCached } = columnsCache<ISubscription>({
    sortedInfo,
    filteredInfo,
    entities,
    locale,
    filterIn,
    onEdit,
    onDelete,
  });

  if (cached) {
    return cached;
  }

  const t = getTranslatedMessage;

  const actionsColumn =
    onEdit || onDelete
      ? {
          ...actionField(),
          render: (record: ISubscription): React.JSX.Element => (
            <div>
              <EditAction
                subject={EAbilitySubjects.SUBSCRIPTION}
                onEdit={onEdit}
                type={'text'}
                entityId={record.id}
              />
              <DeactivateAction<ISubscription>
                subject={EAbilitySubjects.SUBSCRIPTION}
                onDelete={onDelete}
                type={'text'}
                entity={record}
                translateKey={t('subscription')}
              />
            </div>
          ),
        }
      : null;

  const columns = [
    indexColumn,
    {
      title: t('form.title'),
      dataIndex: 'title',
      key: 'title',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'title',
      ) as TColumnFilter<ISubscription>),
      ...columnSorter(sortedInfo, 'title', 'title', 1),
      render: (title: string, record: ISubscription): JSX.Element => (
        <Tooltip title={record.description}>{title}</Tooltip>
      ),
    },
    {
      title: t('price.title'),
      dataIndex: 'price',
      key: 'price.originalPrice',
      width: 100,
      concealable: true,
      render: (price: IPrice): JSX.Element => (
        <>
          {numberToCurrency(
            price.originalPrice,
            locale,
            price.currency as ECurrency,
          )}
        </>
      ),
    },
    {
      title: (
        <Tooltip title={t('price.discount')}>
          <PercentageOutlined />
        </Tooltip>
      ),
      align: 'center',
      dataIndex: 'price',
      key: 'price.discount',
      width: 100,
      concealable: true,
      render: (price: IPrice): JSX.Element | null =>
        price.discount ? (
          <>
            {price.discount.type === 'PERCENT'
              ? `${price.discount.value}%`
              : numberToCurrency(
                  price.discount.value,
                  locale,
                  price.currency as ECurrency,
                )}
          </>
        ) : null,
    },
    {
      title: t('form.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      align: 'center',
      concealable: true,
      render: (status: keyof typeof EStatus): JSX.Element => (
        <StatusTag status={status} />
      ),
    },
    {
      title: t('form.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
      concealable: true,
      render: (type: string): JSX.Element => <Tag>{type}</Tag>,
    },
    {
      title: t('features'),
      key: 'features',
      width: 130,
      align: 'center',
      concealable: true,
      render: (_, record: ISubscription): JSX.Element => (
        <FeatureAssignments subscription={record} />
      ),
    },
    {
      title: t('form.createdAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.createdAt',
      hidden: true,
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.createdAt',
        (createdAt: IMetadata['createdAt']) => tsToLocaleDateTime(createdAt),
      ) as TColumnFilter<ISubscription>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.createdAt', 2),
      render: ({ createdAt }: IMetadata): JSX.Element => (
        <div>{createdAt ? tsToLocaleDateTime(createdAt) : null}</div>
      ),
    },
    {
      title: t('form.updatedAt'),
      width: 200,
      dataIndex: 'metadata',
      align: 'center',
      key: 'metadata.updatedAt',
      concealable: true,
      ...(columnFilter(
        filteredInfo,
        entities,
        'metadata.updatedAt',
        (updatedAt: IMetadata['updatedAt']) => tsToLocaleDateTime(updatedAt),
      ) as TColumnFilter<ISubscription>),
      ...columnSorter(sortedInfo, 'metadata', 'metadata.updatedAt', 3),
      render: ({ updatedAt }: IMetadata): JSX.Element => (
        <div>{updatedAt ? tsToLocaleDateTime(updatedAt) : null}</div>
      ),
    },
  ];

  if (actionsColumn) columns.push(actionsColumn);

  const result = filterIn.length
    ? columns.filter(
        (column) =>
          filterIn.includes(column?.dataIndex as string) ||
          column.dataIndex === 'idx',
      )
    : columns;

  return updateCached(result);
};
